import { TZDate } from '@repo/tzdate';
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { BookingBaseForm } from 'src/components/domain/booking-base/BookingBase';
import { useOnClickBookPackage } from 'src/hooks/domain/activities/useOnClickBookPackage';
import { useWidgetEventEffect } from 'src/hooks/domain/events/useWidgetEventEffect';
import { usePackageQuantities } from 'src/hooks/domain/booking/usePackageQuantities';
import { useLocale } from '@repo/i18n';
import { Package, PackageTicketOptionWithQuantity, ProductInstance } from '@repo/types';
import {
    usePackageProduct,
    usePackageAvailability,
    useAvailablePackageProducts,
    useNextPackageAvailability,
} from '@repo/widget-utils/services/hooks/product';
import { getPackageTicketOptionsWithTours } from '@repo/widget-utils/packages/getPackageTicketOptionsWithTours';
import { validatePackageAvailabilities } from '@repo/widget-utils/packages/validation';
import { groupBy, sum } from 'lodash-es';
import { alpha, Button, Collapse, Stack, Typography } from '@mui/material';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import TourSelectionContainer from 'src/components/domain/package/tour-selection/subcomponents/TourSelectionContainer';
import { getFirstAvailableProducts } from 'src/components/domain/package/tour-selection/TourSelection';
import { PackageSummary } from 'src/components/domain/package/tour-selection/PackageSummary';
import { useBookingContext } from 'src/widgets/BookingContext';
import { setQuantities } from 'src/state/booking/bookingReducer';

interface IProps {
    packageId: string;
    dateSelectionFirst?: boolean;
}

export default function PackageBookingInline(props: IProps): JSX.Element {
    const { packageId, dateSelectionFirst = false } = props;
    const { hasChosenDate, visible, quantities, bookingDispatch } =
        useBookingContext<PackageTicketOptionWithQuantity>();
    const customizations = useCustomizations();
    const { t } = useLocale();

    const { data: pkg = null } = usePackageProduct(packageId);
    const { data: nextPackageAvailability, isLoading: isLoadingNextAvailability } =
        useNextPackageAvailability(packageId);

    useEffect(() => {
        bookingDispatch(setQuantities(pkg?.ticketOptions ?? []));
    }, [pkg, bookingDispatch]);

    const [availabilitySearchPeriod, setAvailabilitySearchPeriod] = useState<{
        startDay: TZDate | null;
        endDay: TZDate | null;
    }>({
        startDay: null,
        endDay: null,
    });

    useEffect(() => {
        if (nextPackageAvailability === undefined) return; // still loading

        if (nextPackageAvailability?.next) {
            const nextDate = new TZDate(nextPackageAvailability.next);
            setAvailabilitySearchPeriod({
                startDay: nextDate.startOf('month'),
                endDay: nextDate.endOf('month'),
            });
        } else {
            // No availability found, fall back to current month
            setAvailabilitySearchPeriod({
                startDay: TZDate.now().startOf('month'),
                endDay: TZDate.now().endOf('month'),
            });
        }
    }, [nextPackageAvailability]);

    const [selectedDate, setSelectedDate] = useState<TZDate | null>(null);
    const onSelectDate = useCallback((date: TZDate | null) => {
        // Packages always start at the beginning of the day, so we need to set the time to 00:00:00
        setSelectedDate(date?.startOf('day') ?? null);
    }, []);

    const { data: pkgAvailability, isLoading: isLoadingAvailabilities } = usePackageAvailability(
        availabilitySearchPeriod.startDay ? pkg : null,
        availabilitySearchPeriod.startDay ?? TZDate.now(),
        availabilitySearchPeriod.endDay ?? TZDate.now().add(1, 'month'),
    );

    const pkgAvailabilityWithQuantitiesCheck = useMemo(
        () =>
            Object.values(groupBy(pkgAvailability, (p) => p.start.format('YYYY-MM-DD'))).flatMap(
                (availabilityDay) => {
                    const isAvailable = quantities.every(
                        (q) =>
                            q.quantity === 0 ||
                            availabilityDay.some((a) =>
                                a.ticketOptions.some(
                                    (to) => to.ticketCategoryId === q.ticketCategoryId,
                                ),
                            ),
                    );
                    if (isAvailable) return availabilityDay;
                    else return [];
                },
            ),
        [pkgAvailability, quantities],
    );

    useWidgetEventEffect(
        (pkg) => ({
            eventType: 'viewItem',
            product: pkg,
            productType: 'Package',
        }),
        pkg,
        visible,
    );

    const packagePrices = useMemo(() => (pkg ? pkg.ticketOptions : []), [pkg]);

    // Enrich quantities with productInstances from availability data
    const priceQuantities = useMemo(() => {
        if (!quantities || !pkgAvailability || pkgAvailability.length === 0) {
            return quantities;
        }

        return quantities.map((q) => ({
            ...q,
            productInstances: pkgAvailability?.filter((p) => p.product?.pkgTicketOptionId === q.id),
        }));
    }, [quantities, pkgAvailability]);

    const onClickBook = useOnClickBookPackage(pkg, packagePrices, selectedDate, onSelectDate);

    // Use new domain hook for package quantities (pass enriched quantities)
    const { inputItems, errors, onChangeQuantity } = usePackageQuantities(priceQuantities);

    // Split errors for BookingBaseForm compatibility
    const totalErrors = errors.filter((e) => e.type === 'total');

    // Package-specific warning label logic
    function getWarningLabel() {
        if (!hasChosenDate) return undefined;
        if (errors.some((e) => e.error === 'minEntrants')) {
            return t.a_minimum_of_participants_is_required_to_book_this_product;
        }
        if (errors.some((e) => e.error === 'capacity')) {
            return t.one_or_more_activities_or_accommodations_dont_have_enough_capacity;
        }
        return undefined;
    }
    const warningLabel = getWarningLabel();

    const [showTourSelection, setShowTourSelection] = useState(false);

    return (
        <Stack width="100%">
            <Typography variant="h2" mb={1}>
                {pkg?.title}
            </Typography>
            <Typography variant="body1" mb={3}>
                {pkg?.shortDescription}
            </Typography>
            <Typography variant="h3" fontSize={24} fontWeight={700} lineHeight={1} m={0}>
                Select date and tickets
            </Typography>
            <Stack
                width="100%"
                bgcolor={customizations.bookingWidgetColor}
                color={customizations.bookingWidgetColorContrast}
                mt={1}
                p={3}
                gap={1}
                borderRadius={`${customizations.borderRadius}px`}
                position="relative"
                boxSizing="border-box"
            >
                <BookingBaseForm
                    backgroundColor={customizations.bookingWidgetColor}
                    color={customizations.bookingWidgetColorContrast}
                    labelColor={customizations.bookingWidgetColorContrast}
                    inputFieldBorder={`1px solid ${alpha(
                        customizations.bookingWidgetColorContrast,
                        0.2,
                    )}`}
                    isPackageCalendar
                    travelerQuantities={priceQuantities}
                    availabilityData={pkgAvailabilityWithQuantitiesCheck}
                    isLoading={isLoadingNextAvailability || isLoadingAvailabilities}
                    availabilitySearchPeriod={availabilitySearchPeriod}
                    setAvailabilitySearchPeriod={setAvailabilitySearchPeriod}
                    selectedDate={selectedDate}
                    setSelectedDate={onSelectDate}
                    totalErrors={totalErrors}
                    dateSelectionFirst={dateSelectionFirst}
                    quantityInputItems={inputItems}
                    onChangeQuantity={onChangeQuantity}
                />
                {warningLabel && (
                    <Typography variant="body2" color="error" textAlign="center" sx={{ mt: 1 }}>
                        {warningLabel}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    onClick={() => setShowTourSelection(true)}
                    sx={{
                        alignSelf: 'flex-end',
                        backgroundColor: customizations.bookingWidgetPrimaryColor,
                        color: customizations.bookingWidgetPrimaryColorContrast,
                        '&:hover': {
                            backgroundColor: alpha(customizations.bookingWidgetPrimaryColor, 0.8),
                            color: customizations.bookingWidgetPrimaryColorContrast,
                        },
                    }}
                    disabled={
                        !quantities.some((q) => q.quantity > 0) ||
                        !selectedDate ||
                        showTourSelection ||
                        errors.length > 0
                    }
                >
                    {t.next}
                </Button>
            </Stack>
            <Collapse in={showTourSelection} timeout={500}>
                {pkg && selectedDate && showTourSelection && (
                    <TourSelection
                        pkg={pkg}
                        date={selectedDate}
                        onBook={(selectedProducts) => {
                            onClickBook(selectedProducts);
                            setSelectedDate(null);
                            bookingDispatch(setQuantities([]));
                            setShowTourSelection(false);
                        }}
                        ticketOptions={quantities}
                    />
                )}
            </Collapse>
        </Stack>
    );
}

function TourSelection({
    pkg,
    date,
    ticketOptions,
    onBook,
}: {
    pkg: Package;
    date: TZDate;
    ticketOptions: PackageTicketOptionWithQuantity[];
    onBook: (selectedProducts: ProductInstance[]) => void;
}) {
    const { data: availabilities } = useAvailablePackageProducts(pkg, date);
    const [selectedAvailabilities, setSelectedAvailabilities] = useState<ProductInstance[]>([]);
    const { t } = useLocale();

    useEffect(() => {
        const products = getFirstAvailableProducts(
            {
                pkg: { pkg, date },
                ticketOptions,
                products: [],
                disablePaymentPlans: true, // Packages are not supported by the membership api
            },
            availabilities,
        );
        if (products.length > 0) setSelectedAvailabilities(products);
    }, [availabilities, date, pkg, ticketOptions]);

    const ticketOptionsWithTours = getPackageTicketOptionsWithTours(ticketOptions, availabilities);

    const selectedTicketOptionsWithTours = getPackageTicketOptionsWithTours(
        ticketOptions,
        selectedAvailabilities,
    );

    const numberOfTravellers = useMemo(() => {
        return sum(ticketOptions.map((q) => q.quantity));
    }, [ticketOptions]);

    const validationErrors = useMemo(() => {
        return validatePackageAvailabilities(selectedAvailabilities, numberOfTravellers, t);
    }, [selectedAvailabilities, numberOfTravellers, t]);

    return (
        <Stack width="100%" pt={4}>
            <Typography variant="h3" fontSize={24} fontWeight={700} lineHeight={1} m={0} pb={1}>
                {t.select_products}
            </Typography>
            <TourSelectionContainer
                products={availabilities}
                selectedAvailabilities={selectedAvailabilities}
                setSelectedAvailabilities={setSelectedAvailabilities}
                ticketOptionsWithTours={ticketOptionsWithTours}
                initiallyExpanded
                validationErrors={validationErrors}
            />
            <Typography
                variant="h3"
                fontSize={24}
                fontWeight={700}
                lineHeight={1}
                m={0}
                pt={4}
                pb={1}
            >
                {t.summary}
            </Typography>
            <PackageSummary
                ticketOptionsWithTours={selectedTicketOptionsWithTours}
                onBook={() => onBook(selectedAvailabilities)}
                validationErrors={validationErrors}
            />
        </Stack>
    );
}
